<script lang="ts" setup>
import { ref, onMounted } from "vue";
import TagItem from "@/components/Reusables/Utils/TagItem.vue";
import UnitPrConstructionUnit from "@/components/Views/CreateConstruction/Content/UnitPrConstructionUnit.vue";
import useLibraryEnums, { KeyedIdentityEnum } from "@/providers/Materials/LibraryEnumsProvider";
import { useCategoryTranslations } from "@/dataProviders/CategoryTranslations";
import i18n from "@/services/i18n";
import { Check, Layers } from "@lucide/vue";

const emits = defineEmits(["onClick"]);

defineProps<{
  material?: Array<any>;
  constructionUnit: number;
}>();

const libraryEnums = useLibraryEnums();
const categoryTranslations = useCategoryTranslations();
const declaredUnitEnums = ref<KeyedIdentityEnum>();

onMounted(async () => {
  declaredUnitEnums.value = await libraryEnums.getDeclaredUnitEnums();
});

const categoryLabel = (category?: string | null) =>
  category ? categoryTranslations.translate(category, [i18n.global.locale.value]).value : "";
</script>

<template>
  <template v-if="material && material.length > 0">
    <div
      v-for="(construction, i) in material"
      :key="i"
      class="flex w-full flex-col">
      <!-- Parent with children - different styling -->
      <div
        v-if="construction.epd?.constructionEpds && construction.epd.constructionEpds.length > 0"
        class="relative flex w-full items-center rounded-t border border-b-0 border-slate-200 bg-slate-200 text-xs">
        <div class="flex h-full w-10 shrink-0 items-center justify-center rounded-tl">
          <Layers
            class="icon-sm text-slate-400"
            :stroke-width="1.5" />
        </div>
        <div class="bg-slate-0 flex w-full items-center gap-4 rounded-tr">
          <div class="flex flex-col gap-1 p-4">
            <!-- Name -->
            <div class="flex items-center gap-4">
              <button
                type="button"
                class="text-text-color hover:text-app-blue-600 focus-ring rounded-4 w-full shrink grow cursor-pointer truncate text-left font-semibold"
                @click="emits('onClick', construction.epd)">
                {{ construction.epd.name }}
              </button>
            </div>
            <!-- Description -->
            <div class="text-type-muted flex gap-4 text-xs">
              <span class="max-w-[400px]">
                {{ construction.epd.description || "" }}
              </span>
            </div>
          </div>
          <div class="ml-auto shrink-0 pr-6">
            <UnitPrConstructionUnit
              v-if="
                declaredUnitEnums &&
                construction.part &&
                construction.epd?.declaredUnit &&
                declaredUnitEnums[construction.epd.declaredUnit.declaredUnit] &&
                declaredUnitEnums[constructionUnit]
              "
              :amount="construction.part.amount"
              :unit="
                declaredUnitEnums[construction.epd.declaredUnit.declaredUnit]?.Enum ||
                declaredUnitEnums[construction.epd.declaredUnit.epdDeclaredUnitType]?.Enum ||
                ''
              "
              :percentage="construction.part.percentage"
              :construction-unit="declaredUnitEnums[constructionUnit]?.Enum || ''" />
          </div>
        </div>
      </div>
      <!-- Leaf item - original styling -->
      <div
        v-else
        class="rounded-4 bg-slate-0 flex w-full border border-slate-200 text-xs">
        <div class="bg-app-green-300 flex w-10 shrink-0 items-center justify-center self-stretch rounded-l">
          <Check
            class="icon-sm text-white"
            :stroke-width="2" />
        </div>
        <div class="flex w-full items-center gap-4 p-2 pr-6">
          <div class="flex h-full grow items-center justify-between gap-1.5">
            <button
              type="button"
              class="text-text-color hover:text-app-blue-600 focus-ring rounded-4 flex shrink grow cursor-pointer flex-col pr-2 text-left font-semibold"
              @click="emits('onClick', construction.epd)">
              {{ construction.epd.name }}

              <span class="text-type-muted text-xs">Lifetime: {{ construction.expectedLifespan }} years</span>
            </button>

            <div class="flex h-fit w-fit flex-wrap gap-1">
              <TagItem
                v-if="construction.epd.category"
                color="label"
                :label="categoryLabel(construction.epd.category)" />
              <template v-else>
                <TagItem
                  v-for="tag in construction.epd.tags"
                  :key="tag"
                  color="label"
                  :label="tag" />
              </template>
            </div>
          </div>
          <div class="ml-auto shrink-0">
            <UnitPrConstructionUnit
              v-if="
                declaredUnitEnums &&
                construction.part &&
                construction.epd?.declaredUnit &&
                declaredUnitEnums[construction.epd.declaredUnit.declaredUnit] &&
                declaredUnitEnums[constructionUnit]
              "
              :amount="construction.part.amount"
              :unit="
                declaredUnitEnums[construction.epd.declaredUnit.declaredUnit]?.Enum ||
                declaredUnitEnums[construction.epd.declaredUnit.epdDeclaredUnitType]?.Enum ||
                ''
              "
              :percentage="construction.part.percentage"
              :construction-unit="declaredUnitEnums[constructionUnit]?.Enum || ''" />
          </div>
        </div>
      </div>
      <!-- Recursively render nested construction EPDs -->
      <div
        v-if="construction.epd?.constructionEpds && construction.epd.constructionEpds.length > 0"
        class="flex flex-col">
        <ConstructionEpd
          :material="construction.epd.constructionEpds"
          :construction-unit="constructionUnit"
          @on-click="emits('onClick', $event)" />
      </div>
    </div>
  </template>
</template>
